import React, { useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    TextFieldProps,
} from '@material-ui/core';
import { noop } from '../util/functions';

export type UsePromptOptions = {
    submitLabel?: string;
    cancelLabel?: string;
}

export interface UsePromptResponse {
    render(): React.ReactNode;
    prompt(message: string, defaultValue?: string, label?: string, title?: string, textFieldProps?: TextFieldProps, allowEmpty?: boolean): Promise<string|null>;
}

export const usePrompt = (options: UsePromptOptions = {}): UsePromptResponse => {
    const { submitLabel = '', cancelLabel = '' } = options || {};
    const [ open, setOpen ] = useState(false);
    const [ value, setValue ] = useState('');
    const textFieldPropsRef = useRef({});
    const titleRef = useRef('');
    const messageRef = useRef('');
    const labelRef = useRef('');
    const allowEmptyRef = useRef(false);
    const clickHandlerRef = useRef<(value: string|null) => () => unknown>(() => noop);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        clickHandlerRef.current(value);
    };

    const render: UsePromptResponse['render'] = () => (
        <Dialog
            open={open}
            onClose={clickHandlerRef && clickHandlerRef.current ? clickHandlerRef.current(null) : null}
            fullWidth
        >
            <form onSubmit={handleSubmit}>
                {titleRef && titleRef.current && <DialogTitle>{titleRef.current}</DialogTitle>}
                <DialogContent>
                    {messageRef && messageRef.current && (
                        <DialogContentText>{messageRef.current}</DialogContentText>
                    )}
                    <TextField
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        fullWidth
                        label={labelRef.current}
                        autoFocus {...textFieldPropsRef.current}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={clickHandlerRef && clickHandlerRef.current ? clickHandlerRef.current(null) : null}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        color="primary"
                        onClick={clickHandlerRef && clickHandlerRef.current ? clickHandlerRef.current(value) : null}
                        type="submit"
                        disabled={!value || allowEmptyRef.current}
                    >
                        {submitLabel}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );

    const prompt: UsePromptResponse['prompt'] = async (message, defaultValue = '', label = '', title = '', textFieldProps = {}, allowEmpty = false) => {
        messageRef.current = label ? message : null;
        labelRef.current = label || message;
        titleRef.current = title;
        textFieldPropsRef.current = textFieldProps;
        allowEmptyRef.current = allowEmpty;
        setValue(defaultValue);
        setOpen(true);

        return new Promise((res) => {
            clickHandlerRef.current = (value: string) => () => {
                setOpen(false);
                res(value === null || value.trim() === '' ? null : value);
            };
        });
    };

    return {
        render,
        prompt,
    };
};
